import React, { useEffect, useState, memo, useMemo } from "react";
import Modal from "@/components/Modal/Modal";
import type { RootState } from "@/store/rootReducer";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/store/hooks";
import { getWorkStation } from "@/store/slices/userSlice";
import { useForm } from "react-hook-form";
import FormControl from "@/components/FormControl/FormControl";

interface PunchInReportModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  onSubmit: (report: string, workstation: string) => void;
  isPunchReport?: boolean;
  isWorkstation?: boolean;
  isLoading?: boolean;
}

interface FormValues {
  report: string;
  workstationId: string;
}

const PunchInReportModal: React.FC<PunchInReportModalProps> = ({
  modalOpen,
  closeModal,
  onSubmit,
  isPunchReport = false,
  isWorkstation = false,
  isLoading = false,
}) => {
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    control,
    reset,
  } = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: {
      report: "",
      workstationId: "",
    },
  });

  const workstationItems = useSelector(
    (store: RootState) => store?.user?.workStationList?.[0]?.Items || []
  );

  const workstationOptions = useMemo(
    () =>
      workstationItems.map((s: { Name: string; Id: string }) => ({
        label: s.Name,
        value: s.Id,
      })),
    [workstationItems]
  );

  useEffect(() => {
    if (modalOpen && isWorkstation) {
      dispatch(getWorkStation());
    }
  }, [modalOpen, isWorkstation, dispatch]);

  useEffect(() => {
    if (!modalOpen) {
      reset({
        report: "",
        workstationId: "",
      });
    }
  }, [modalOpen, reset]);

  const submitHandler = (data: FormValues) => {
    let reportData = data.report?.trim();
    let workstationId = data.workstationId;

    if (isWorkstation && !workstationId) return;

    if (isPunchReport) {
      if (!reportData) return;
      if (reportData.length > 500) return;
    }

    onSubmit(reportData || "", workstationId || "");
  };

  return (
    <Modal
      modalOpen={modalOpen}
      closeModal={closeModal}
      heading="Punch In Report"
      saveButtonText="Done"
      width="500px"
      onSave={handleSubmit(submitHandler)}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-3 mt-2">
        {/* Report */}
        {isPunchReport && (
          <FormControl
            name="report"
            control={control}
            controlType="textarea"
            label="Report"
            minRows={3}
            maxRows={3}
            maxChar={500}
            isHint
            required
            rules={{
              required: "Report is required",
              maxLength: {
                value: 500,
                message: "Max 500 characters allowed",
              },
            }}
          />
        )}

        {/* Workstation */}
        {isWorkstation && (
          <FormControl
            name="workstationId"
            control={control}
            controlType="select"
            placeholder="Select Workstation"
            options={workstationOptions || []}
            idKey="Id"
            valueKey="Name"
            required
            rules={{
              required: "Workstation is required"
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default memo(PunchInReportModal);
